// Free Guide: a short opt-in for a free guide or checklist. Visual language
// from Phantom Profits (board #32: soft gray paper, a yellow-boxed phrase,
// avatar proof), the Agency Accelerator buy panel (#43: the form card with a
// flat offset shadow) and Content to Cashflow (#37: mono labels, product shot).
import type { PageStarter } from './types';
import { AVATAR, AVATARS, LOGOS, footer, img } from './assets';

export const leadMagnet: PageStarter = {
  key: 'lead-magnet',
  name: 'Free guide',
  description: 'Lead magnet opt-in: the guide up front, the form right under it, what is inside, the author and reviews.',
  category: 'leads',
  theme: { style: 'paper', font: 'inter', brand_color: '#1F1F1F', button_color: '#FACA32', archetype: 'lead-magnet' },
  form: [{ key: 'full_name', required: false }, 'email'],
  formTitle: 'Send me the free guide',
  formDescription: 'It lands in your inbox in about a minute.',
  formButton: 'Send me the free guide',
  content: {
    sections: [
      { id: 'lm_nav', type: 'nav', logo: true, heading: 'Reviewly' },
      { id: 'lm_hero', type: 'hero', text_size: 'md', space: 'tight', eyebrow: 'Free 24-page guide',
        headline: 'Get more five-star reviews in 30 days', highlight: 'five-star reviews',
        subheadline: 'The exact texts and emails local businesses use to ask happy customers for a review, and when to send them.',
        cta_label: 'Send me the free guide', cta_href: '#form', cta_note: 'No spam. Unsubscribe in one click.',
        proof_avatars: AVATARS(1, 4), rating: 5, proof_text: '3,100+ owners downloaded it',
        image_url: img('mock-guide'), image_pos: 'side' },
      { id: 'lm_form', type: 'form', after_submit: 'thank_you', space: 'tight' },
      { id: 'lm_logos', type: 'logos', heading: 'Readers run businesses like', logos: LOGOS(6), text_size: 'sm' },
      { id: 'lm_inside', type: 'features', heading: 'What is inside the guide',
        features: [
          { icon: '01', title: 'The perfect moment to ask', body: 'The three moments customers are happiest to leave a review.' },
          { icon: '02', title: 'Copy-and-paste texts', body: 'Five short review requests that sound like a person, not a bot.' },
          { icon: '03', title: 'The follow-up email', body: 'One gentle reminder that doubles your response rate.' },
          { icon: '04', title: 'Handling bad reviews', body: 'A calm reply template that wins back trust in public.' },
          { icon: '05', title: 'Your 30-day plan', body: 'A simple calendar to go from a handful of reviews to dozens.' },
          { icon: '06', title: 'Tracking sheet', body: 'See who you asked, who replied and what worked.' },
        ] },
      { id: 'lm_peek', type: 'split', image_url: img('guide-page-2'), image_side: 'left',
        eyebrow: 'A look inside', heading: 'Short pages. Real scripts. No fluff.',
        body: 'Every page is something you can use the same day: a message to send, a moment to send it, and what to say if they do not reply.',
        items: ['24 pages, readable in 15 minutes', 'Works for any local business', 'Printable checklist included'] },
      { id: 'lm_why', type: 'features', variant: 'list', bg: 'ink', inset: true, eyebrow: 'Why this works', heading: 'Most happy customers never review. Not because they do not want to.',
        features: [
          { icon_name: 'clock', title: 'You ask at the wrong time', body: 'Asking a week later means the good feeling has faded.' },
          { icon_name: 'message', title: 'The ask is too much work', body: 'One tap beats a long email with three links.' },
          { icon_name: 'repeat', title: 'There is no reminder', body: 'A single friendly nudge brings in half of all reviews.' },
          { icon_name: 'star', title: 'Reviews compound', body: 'More recent reviews means more calls from search.' },
        ] },
      { id: 'lm_author', type: 'bio', photo_url: img('portrait-08'), image_side: 'left',
        eyebrow: 'Who wrote it', heading: 'Hi, I am Sam Rivera.',
        body: 'I help local businesses get found and chosen. This guide is the routine my clients use to collect reviews every week without feeling pushy.',
        stats: [{ value: '1,400+', label: 'businesses helped' }, { value: '38', label: 'average new reviews in 30 days' }] },
      { id: 'lm_reviews', type: 'testimonials', heading: 'What readers say',
        quotes: [
          { rating: 5, quote: 'We went from 19 reviews to 61 in a month. The first text template did most of the work.', name: 'Carla Diaz', role: 'Owner, Diaz Dental', photo_url: AVATAR(5) },
          { rating: 5, quote: 'Short, useful and free. I printed the checklist and put it by the register.', name: 'Ken Mori', role: 'Owner, Mori Bikes', photo_url: AVATAR(6) },
          { rating: 5, quote: 'The bad review reply saved me from a very public mistake. Worth it for that page alone.', name: 'Jen Walsh', role: 'Salon owner', photo_url: AVATAR(7) },
        ] },
      { id: 'lm_cta', type: 'cta', bg: 'ink', inset: true, heading: 'Your next five-star review is one text away', highlight: 'one text away',
        subheadline: 'Get the free guide and send your first request today.', label: 'Send me the free guide', href: '#form', cta_note: 'No spam. Unsubscribe in one click.' },
    ],
    thank_you: {
      headline: 'Check your inbox', subhead: 'The guide is on its way to your email.',
      sections: [
        { id: 'lm_ty_h', type: 'hero', headline: 'Check your inbox', subheadline: 'The guide is on its way. It usually arrives in about a minute.' },
        { id: 'lm_ty_t', type: 'text', align: 'center', heading: 'Want help putting it to work?', body: 'Book a free 15-minute call and we will set up your first review request together.' },
        { id: 'lm_ty_b', type: 'booking', booking_slug: '' },
      ],
    },
    footer: footer('lm_ft', 'Reviewly'),
  },
};
